import {
  SettingsLocation,
  WorkOrderStatus,
  ApplicationUser,
  WorkOrderPriorityCode,
  ServiceCodes,
  CustomerListHeader,
  PaymentMethod,
  AltStockLocation,
  PartListHeader,
  UnitListHeader,
  MiscCharge,
  Zone,
  Country,
  State,
  CityStateZip,
} from "../../../models/lookupTypes";
import {
  StandardJobCodeType,
  StandardJobCodeTypeList,
  WorkOrder,
} from "../models/workOrderTypes";
import type {
  LocationBusinessHours,
  LocationHoliday,
  EmployeeSchedule,
  EmployeeScheduleException,
} from "../../technicianCalendar/lib/models";
import { get, post } from "../../../api/index";
import { getCache, setCache } from "../../common/Utils/cacheUtil";

const controllerUrl = "api/Lookup/";

const lookupCacheUser = (): string =>
  localStorage.getItem("username") || "currentUserIdentifier";

const LOOKUP_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24; // 24 hours

export const LookupAPI = {
  getWorkOrderStatuses: async (): Promise<WorkOrderStatus[]> => {
    try {
      const response = await get(`${controllerUrl}GetWorkOrderStatuses`);
      return response.data.content;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },
  getLocations: async (): Promise<SettingsLocation[]> => {
    try {
      const response = await get(`${controllerUrl}GetLocations`);
      return response.data.content;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },
  getTechniciansForFilters: async (): Promise<ApplicationUser[]> => {
    try {
      const response = await get(`${controllerUrl}GetTechnicians`);
      return response.data.content;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getTechnicians: async (): Promise<ApplicationUser[]> => {
    try {
      const response = await get(`${controllerUrl}GetTechnicians`);
      return response.data.content;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getSalesPersons: async (): Promise<ApplicationUser[]> => {
    try {
      const response = await get(`${controllerUrl}GetSalesPersons`);
      return response.data.content;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },
  getStandardJobSourceList: async (): Promise<string[]> => {
    try {
      const response = await get(`${controllerUrl}GetSdJobSources`);
      return response.data.content;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },
  getServiceCodes: async (): Promise<ServiceCodes[]> => {
    try {
      const response = await get(`${controllerUrl}GetSegmentServiceCodes`);
      return response.data.content;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getStandardJobCodeType: async (): Promise<StandardJobCodeType[]> => {
    try {
      const response = StandardJobCodeTypeList;
      return response;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },
  getWorkOrderPriorityList: async (): Promise<WorkOrderPriorityCode[]> => {
    try {
      const response = await get(`${controllerUrl}GetWorkOrderPriorityList`);
      return response.data.content;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getWorkOrder: async (
    workOrderIdentity: number,
  ): Promise<WorkOrder | null> => {
    try {
      const { data } = await get(
        `${controllerUrl}GetWorkOrderByIdentity?workOrderIdentity=${workOrderIdentity}`,
      );
      return data;
    } catch (err) {
      //console.log(err);
      return null;
    }
  },
  getAllCustomerListHeaders: async (): Promise<
    Record<string, CustomerListHeader[]>
  > => {
    try {
      const response = await get(`${controllerUrl}GetAllCustomerListHeaders`);
      return response.data.content || {};
    } catch (err) {
      //console.log(err);
      return {};
    }
  },
  getPaymentMethods: async (): Promise<PaymentMethod[]> => {
    try {
      const response = await get(`${controllerUrl}GetPaymentMethods`);
      return response.data.content;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },
  getAltStockLocations: async (): Promise<AltStockLocation[]> => {
    try {
      const response = await get(`${controllerUrl}GetAltStockLocations`);
      return response.data.content;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },
  getAllPartListHeaders: async (): Promise<
    Record<string, PartListHeader[]>
  > => {
    try {
      const response = await get(`${controllerUrl}GetAllPartListHeaders`);
      return response.data.content || {};
    } catch (err) {
      //console.log(err);
      return {};
    }
  },
  getMiscCharges: async (): Promise<MiscCharge[]> => {
    try {
      const response = await get(`${controllerUrl}GetMiscCharges`);
      return response.data.content;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },
  getAllUnitListHeaders: async (): Promise<
    Record<string, UnitListHeader[]>
  > => {
    try {
      const response = await get(`${controllerUrl}GetAllUnitListHeaders`);
      return response.data.content || {};
    } catch (err) {
      //console.log(err);
      return {};
    }
  },
  getZones: async (): Promise<Zone[]> => {
    try {
      const response = await get(`${controllerUrl}GetZones`);
      return response.data.content;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  // Location Business Hours
  getAllLocationBusinessHours: async (): Promise<LocationBusinessHours[]> => {
    try {
      const response = await get(`${controllerUrl}GetAllLocationBusinessHours`);
      return response.data.content || [];
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getLocationBusinessHours: async (
    locationIds: number[],
  ): Promise<LocationBusinessHours[]> => {
    try {
      const response = await post(`${controllerUrl}GetLocationBusinessHours`, {
        locationIds,
      });
      return response.data.content || [];
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  // Location Holidays
  getAllLocationHolidays: async (): Promise<LocationHoliday[]> => {
    try {
      const response = await get(`${controllerUrl}GetAllLocationHolidays`);
      return response.data.content || [];
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getLocationHolidays: async (
    locationIds?: number[],
    fromDate?: Date,
    toDate?: Date,
  ): Promise<LocationHoliday[]> => {
    try {
      const response = await post(`${controllerUrl}GetLocationHolidays`, {
        locationIds,
        fromDate: fromDate?.toISOString(),
        toDate: toDate?.toISOString(),
      });
      return response.data.content || [];
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  // Location Business Hour Exceptions
  getAllLocationBusinessHourExceptions: async (): Promise<any[]> => {
    try {
      const response = await get(
        `${controllerUrl}GetAllLocationBusinessHourExceptions`,
      );
      return response.data.content || [];
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getLocationBusinessHourExceptions: async (
    locationIds?: number[],
    fromDate?: Date,
    toDate?: Date,
  ): Promise<any[]> => {
    try {
      const response = await post(
        `${controllerUrl}GetLocationBusinessHourExceptions`,
        {
          locationIds,
          fromDate: fromDate?.toISOString(),
          toDate: toDate?.toISOString(),
        },
      );
      return response.data.content || [];
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  // Employee Schedules
  getAllEmployeeSchedules: async (): Promise<EmployeeSchedule[]> => {
    try {
      const response = await get(`${controllerUrl}GetAllEmployeeSchedules`);
      return response.data.content || [];
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getEmployeeSchedules: async (
    locationId?: number,
    technicianId?: number,
  ): Promise<EmployeeSchedule[]> => {
    try {
      const response = await post(`${controllerUrl}GetEmployeeSchedules`, {
        locationIds: locationId ? [locationId] : null,
        technicianId,
      });
      return response.data.content || [];
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  // Employee Schedule Exceptions
  getAllEmployeeScheduleExceptions: async (
    technicianId?: number,
  ): Promise<EmployeeScheduleException[]> => {
    try {
      const params = technicianId ? `?technicianId=${technicianId}` : "";
      const response = await get(
        `${controllerUrl}GetAllEmployeeScheduleExceptions${params}`,
      );
      return response.data.content || [];
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getEmployeeScheduleExceptions: async (
    technicianId?: number,
    fromDate?: Date,
    toDate?: Date,
  ): Promise<EmployeeScheduleException[]> => {
    try {
      const response = await post(
        `${controllerUrl}GetEmployeeScheduleExceptions`,
        {
          technicianId,
          fromDate: fromDate?.toISOString(),
          toDate: toDate?.toISOString(),
        },
      );
      return response.data.content || [];
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getCustomerZone: async (): Promise<Zone[]> => {
    const user = lookupCacheUser();
    const cached = getCache<Zone[]>(
      "customerZones",
      user,
      LOOKUP_CACHE_MAX_AGE_MS,
    );
    if (cached && cached.length > 0) {
      return cached;
    }
    try {
      const response = await get(`${controllerUrl}GetCustomerZone`);
      const data: Zone[] = response.data.content || [];
      if (data.length > 0) {
        setCache("customerZones", data, user);
      }
      return data;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getCountries: async (): Promise<Country[]> => {
    const user = lookupCacheUser();
    const cached = getCache<Country[]>(
      "countries",
      user,
      LOOKUP_CACHE_MAX_AGE_MS,
    );
    debugger;
    if (cached && cached.length > 0) {
      return cached;
    }
    try {
      const response = await get(`${controllerUrl}GetCountries`);
      const data: Country[] = response.data.content || [];
      if (data.length > 0) {
        setCache("countries", data, user);
      }
      return data;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getStates: async (): Promise<State[]> => {
    const user = lookupCacheUser();
    const cached = getCache<State[]>("states", user, LOOKUP_CACHE_MAX_AGE_MS);
    if (cached && cached.length > 0) {
      return cached;
    }
    try {
      const response = await get(`${controllerUrl}GetStates`);
      const data: State[] = response.data.content || [];
      if (data.length > 0) {
        setCache("states", data, user);
      }
      return data;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  getCityStateZip: async (
    locationIdentity: number,
    cityName?: string,
  ): Promise<CityStateZip[]> => {
    const search = `%${(cityName ?? "").trim()}%`;
    try {
      // Matches LookupController.GetZipCodeByCity(string cityName, int locationId)
      const response = await get(`${controllerUrl}GetZipCodeByCity`, {
        params: {
          cityName: search,
          locationId: locationIdentity,
        },
      });
      const data: CityStateZip[] = response.data.content || [];
      return data;
    } catch (err) {
      //console.log(err);
      return [];
    }
  },

  /** Distinct InvoiceHeader.Status values (excludes voided/archived/finalized per API query). */
  getWoDocumentStatuses: async (): Promise<string[]> => {
    const user = lookupCacheUser();
    const cached = getCache<string[]>(
      "woDocStatuses",
      user,
      LOOKUP_CACHE_MAX_AGE_MS,
    );
    if (cached && cached.length > 0) {
      return cached;
    }
    try {
      const response = await get(`${controllerUrl}GetWoDocumetnStatus`);
      const data: string[] = response.data.content || [];
      if (data.length > 0) {
        setCache("woDocStatuses", data, user);
      }
      return data;
    } catch (err) {
      return [];
    }
  },
};
export default LookupAPI;
